import { ApiError } from './types'

/**
 * Cliente HTTP do app.
 *
 * Responsabilidades: montar a URL, anexar credenciais, serializar o corpo e
 * normalizar erros para `ApiError`. Nada além disso — regra de negócio fica
 * nos serviços.
 */

const BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? '/api').replace(/\/$/, '')

/** Onde o token fica quando o backend devolve JWT em vez de cookie httpOnly. */
const TOKEN_STORAGE_KEY = 'mp.access_token'

export const tokenStorage = {
  get(): string | null {
    try {
      return localStorage.getItem(TOKEN_STORAGE_KEY)
    } catch {
      return null
    }
  },
  set(token: string | null) {
    try {
      if (token) localStorage.setItem(TOKEN_STORAGE_KEY, token)
      else localStorage.removeItem(TOKEN_STORAGE_KEY)
    } catch {
      /* modo privado / storage bloqueado: segue sem persistir */
    }
  },
}

type QueryValue = string | number | boolean | null | undefined

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  /** Vira query string; chaves com valor null/undefined são omitidas. */
  query?: Record<string, QueryValue>
  /** Objeto serializado como JSON, ou FormData enviado cru (upload). */
  body?: unknown
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = `${BASE_URL}${path}`
  if (!query) return url

  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value === null || value === undefined || value === '') continue
    search.set(key, String(value))
  }
  const qs = search.toString()
  return qs ? `${url}?${qs}` : url
}

async function parseError(response: Response): Promise<ApiError> {
  let message = `Erro ${response.status}`
  let code = 'unknown_error'
  let fieldErrors: Record<string, string> | undefined

  try {
    const data = (await response.json()) as {
      message?: string
      error?: string
      code?: string
      fieldErrors?: Record<string, string>
    }
    message = data.message ?? data.error ?? message
    code = data.code ?? code
    fieldErrors = data.fieldErrors
  } catch {
    /* resposta sem JSON — mantém a mensagem genérica */
  }

  if (response.status === 401) code = 'unauthorized'
  return new ApiError(message, response.status, code, fieldErrors)
}

export async function request<T>(
  path: string,
  { query, body, headers, ...init }: RequestOptions = {},
): Promise<T> {
  const isFormData = body instanceof FormData
  const token = tokenStorage.get()

  const finalHeaders = new Headers(headers)
  if (!isFormData && body !== undefined) {
    finalHeaders.set('Content-Type', 'application/json')
  }
  if (token) finalHeaders.set('Authorization', `Bearer ${token}`)

  let response: Response
  try {
    response = await fetch(buildUrl(path, query), {
      ...init,
      headers: finalHeaders,
      // Mantém o cookie de sessão caso o backend opte por cookie httpOnly.
      credentials: 'include',
      body: isFormData ? body : body !== undefined ? JSON.stringify(body) : undefined,
    })
  } catch {
    throw new ApiError(
      'Não foi possível conectar. Verifique sua internet e tente de novo.',
      0,
      'network_error',
    )
  }

  if (!response.ok) throw await parseError(response)

  if (response.status === 204) return undefined as T
  const text = await response.text()
  return (text ? JSON.parse(text) : undefined) as T
}

export const http = {
  get: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PUT', body }),
  delete: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'DELETE' }),
}
